import has from "lodash/has";
import { GetServerSideProps, NextPage } from "next";
import { NextSeo } from "next-seo";
import dynamic from "next/dynamic";
import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { initProposal } from "../../src/features/proposal";
import { withAppVariables } from "@/lib/app-variables/with-app-variables";
import { CacheMode, type DemoTypes } from "@/constants";
import { isValidCacheMode, isValidDemoType } from "@/features/cache-mode/helpers";

const ProposalDeck = dynamic(() => import("../../src/ProposalDeck"), { ssr: false });

interface ServerSideProps {
  reportHash: string;
  printMode: boolean;
  cacheMode?: CacheMode;
  demoType?: DemoTypes;
}

const ProposalApp: NextPage<ServerSideProps> = ({ reportHash, printMode, demoType, cacheMode }) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (typeof window === "undefined") return;
    dispatch(initProposal({ id: reportHash, demoType, cacheMode }));
  }, [dispatch, reportHash, demoType, cacheMode]);

  return (
    <>
      <ProposalDeck reportHash={reportHash} printMode={printMode} />
      <CustomPageTitle />
    </>
  );
};

export default ProposalApp;

export const getServerSideProps: GetServerSideProps<ServerSideProps> = async ctx => {
  const props: ServerSideProps = {
    reportHash: ctx.query.reportHash as string,
    printMode: has(ctx.query, "print-pdf"),
    ...withAppVariables(),
  };

  const cacheMode = ctx.query["cacheMode"] as string;

  if (isValidCacheMode(cacheMode)) {
    props.cacheMode = cacheMode;
  }

  const typeParam = Array.isArray(ctx.query.type) ? ctx.query.type[0] : ctx.query.type;
  const demoType = typeParam as DemoTypes | undefined;

  if (isValidDemoType(demoType)) {
    props.demoType = demoType;
  }

  return {
    props,
  };
};

function CustomPageTitle() {
  // Can't use the repeatable selectors here because proposal might not be defined yet
  const customer = useAppSelector(state => state.proposal.data?.customer);

  return <NextSeo title={customer ? `${customer.firstName} ${customer.lastName}` : undefined} />;
}
